import {
  FEATURED_INDUSTRIES,
  OTHER_INDUSTRY,
} from "../constants";
import {
  STEP_INDUSTRY,
  STEP_LOCATION,
  STEP_NAME,
  STEP_REVENUE,
  STEP_CRM,
} from "../onboardingShared";
import {
  ALL_INDUSTRY_OPTIONS,
  ALL_LOCATION_OPTIONS,
  formatRevenueDisplay,
  isFullRevenueRange,
} from "./questionFlow";
import { parseRevenueMillions, REVENUE_MAX, REVENUE_MIN } from "../../../helper/rangeFilter";
import { parseCrmStepAnswer } from "./crmStep";

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^\w\s+$]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const splitCandidates = (text) =>
  String(text || "")
    .split(/\s*(?:,|;|\band\b|\+|\/)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);

// Location labels carry a type prefix ("STATE: Texas", "COUNTY: Texas County (MO)").
// Users type the name, so match on the label without that prefix.
const TYPE_PREFIX = /^[A-Za-z][A-Za-z ]*:\s*/;
const isState = (option) => /^state\s*:/i.test(String(option || ""));
const normalizeOption = (option) => normalize(String(option || "").replace(TYPE_PREFIX, ""));

const findBestMatch = (raw, options) => {
  const needle = normalizeOption(raw);
  if (!needle) return null;

  // An exact name wins over any substring match; among exact names prefer a
  // state over a same-named county or city.
  const exactMatches = options.filter((option) => normalizeOption(option) === needle);
  const exact = exactMatches.find(isState) || exactMatches[0];
  if (exact) return exact;

  const startsWith = options.find((option) => normalizeOption(option).startsWith(needle));
  if (startsWith && needle.length >= 3) return startsWith;

  const includes = options.find((option) => {
    const hay = normalizeOption(option);
    return hay.includes(needle) || needle.includes(hay);
  });
  if (includes && needle.length >= 4) return includes;

  return null;
};

const matchMany = (text, options) => {
  const parts = splitCandidates(text);
  const matched = [];
  const seen = new Set();

  parts.forEach((part) => {
    const hit = findBestMatch(part, options);
    if (hit && !seen.has(hit)) {
      seen.add(hit);
      matched.push(hit);
    }
  });

  // Whole-string fallback when separators weren't used.
  if (matched.length === 0) {
    const whole = findBestMatch(text, options);
    if (whole) matched.push(whole);
  }

  return matched;
};

const ANY_REVENUE_PATTERNS = [
  /^any(\s+size)?$/i,
  /^no\s*(preference|limit|filter)?$/i,
  /^all$/i,
  /^doesn'?t\s+matter$/i,
  /^full\s+range$/i,
  /^unlimited$/i,
];

/**
 * Build a revenue answer from dollar bounds (null = open-ended), clamping to the
 * slider range and collapsing the extremes to null for query parity with form
 * onboarding.
 */
export const buildRevenueAnswer = (lower, upper) => {
  const clamp = (amount) => Math.min(Math.max(amount, REVENUE_MIN), REVENUE_MAX);
  const nextLower = lower == null ? null : clamp(lower);
  const nextUpper = upper == null ? null : clamp(upper);
  const value = {
    revenueLowerBound: nextLower == null || isFullRevenueRange(nextLower, null) ? null : nextLower,
    revenueUpperBound: nextUpper == null || isFullRevenueRange(null, nextUpper) ? null : nextUpper,
  };
  if (nextLower != null && nextLower <= REVENUE_MIN) value.revenueLowerBound = null;
  if (nextUpper != null && nextUpper >= REVENUE_MAX) value.revenueUpperBound = null;
  return {
    ok: true,
    value,
    display: formatRevenueDisplay(value.revenueLowerBound, value.revenueUpperBound),
  };
};

/** Build an industry answer from exact option labels, split featured vs other. */
export const buildIndustryAnswer = (matched) => {
  const featuredSet = new Set(FEATURED_INDUSTRIES);
  const featured = matched.filter((label) => featuredSet.has(label));
  const other = matched.filter((label) => !featuredSet.has(label));
  return {
    ok: true,
    value: {
      industryInclude: featured,
      otherIndustries: other,
      otherSelected: other.length > 0,
    },
    display: matched.join(", "),
  };
};

/** Build a location answer from exact option labels. */
export const buildLocationAnswer = (matched) => ({
  ok: true,
  value: { locations: matched },
  display: matched.join(", "),
});

const parseRevenueText = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return { ok: false };

  if (ANY_REVENUE_PATTERNS.some((pattern) => pattern.test(raw))) {
    return {
      ok: true,
      value: { revenueLowerBound: null, revenueUpperBound: null },
      display: "Any size",
    };
  }

  const underMatch = raw.match(/^(?:under|below|less\s+than|<)\s*(.+)$/i);
  if (underMatch) {
    const upper = parseRevenueMillions(underMatch[1]);
    if (!Number.isFinite(upper) || upper <= 0) return { ok: false };
    const clamped = Math.min(Math.max(upper, REVENUE_MIN), REVENUE_MAX);
    return {
      ok: true,
      value: { revenueLowerBound: null, revenueUpperBound: clamped },
      display: formatRevenueDisplay(null, clamped),
    };
  }

  const overMatch = raw.match(/^(?:over|above|more\s+than|>|at\s+least)\s*(.+)$/i);
  if (overMatch) {
    const lower = parseRevenueMillions(overMatch[1]);
    if (!Number.isFinite(lower) || lower <= 0) return { ok: false };
    const clamped = Math.min(Math.max(lower, REVENUE_MIN), REVENUE_MAX);
    return {
      ok: true,
      value: { revenueLowerBound: clamped, revenueUpperBound: null },
      display: formatRevenueDisplay(clamped, null),
    };
  }

  const plusMatch = raw.match(/^(.+?)\s*\+$/);
  if (plusMatch) {
    const lower = parseRevenueMillions(plusMatch[1]);
    if (!Number.isFinite(lower) || lower <= 0) return { ok: false };
    const clamped = Math.min(Math.max(lower, REVENUE_MIN), REVENUE_MAX);
    return {
      ok: true,
      value: { revenueLowerBound: clamped, revenueUpperBound: null },
      display: formatRevenueDisplay(clamped, null),
    };
  }

  const rangeMatch = raw.match(/^(.+?)\s*(?:-|–|—|to)\s*(.+)$/i);
  if (rangeMatch) {
    const lower = parseRevenueMillions(rangeMatch[1]);
    const upper = parseRevenueMillions(rangeMatch[2]);
    if (!Number.isFinite(lower) || !Number.isFinite(upper)) return { ok: false };
    if (lower <= 0 || upper <= 0 || lower > upper) return { ok: false };
    return buildRevenueAnswer(lower, upper);
  }

  // Single amount → treat as minimum.
  const single = parseRevenueMillions(raw);
  if (Number.isFinite(single) && single > 0) {
    const clamped = Math.min(Math.max(single, REVENUE_MIN), REVENUE_MAX);
    return {
      ok: true,
      value: { revenueLowerBound: clamped, revenueUpperBound: null },
      display: formatRevenueDisplay(clamped, null),
    };
  }

  return { ok: false };
};

/**
 * Parse a free-text (or chip) answer for the active onboarding step.
 * Returns { ok, value, display } or { ok: false }.
 */
export const parseOnboardingAnswer = (step, text, { industryOptions, locationOptions } = {}) => {
  const industries = industryOptions?.length ? industryOptions : ALL_INDUSTRY_OPTIONS;
  const locations = locationOptions?.length ? locationOptions : ALL_LOCATION_OPTIONS;
  const raw = String(text || "").trim();

  if (step === STEP_CRM) return parseCrmStepAnswer([], raw);

  if (step === STEP_INDUSTRY) {
    if (!raw) return { ok: false };

    const wantsOther = normalize(raw) === normalize(OTHER_INDUSTRY);
    if (wantsOther) {
      return {
        ok: true,
        value: {
          industryInclude: [],
          otherIndustries: [],
          otherSelected: true,
        },
        display: OTHER_INDUSTRY,
        needsFollowUp: "other-industry",
      };
    }

    const matched = matchMany(raw, [...FEATURED_INDUSTRIES, ...industries]);
    if (matched.length === 0) return { ok: false };
    return buildIndustryAnswer(matched);
  }

  if (step === STEP_LOCATION) {
    if (!raw) return { ok: false };
    const matched = matchMany(raw, locations);
    if (matched.length === 0) return { ok: false };
    return buildLocationAnswer(matched);
  }

  if (step === STEP_REVENUE) {
    return parseRevenueText(raw);
  }

  if (step === STEP_NAME) {
    // Empty is allowed — form onboarding accepts the suggestion at finish time.
    const name = raw;
    return {
      ok: true,
      value: { searchName: name },
      display: name || "Use suggested name",
    };
  }

  return { ok: false };
};

/** Merge a parsed answer into the onboarding answers object. */
export const applyParsedAnswer = (answers, step, parsed) => {
  if (!parsed?.ok) return answers;
  const next = { ...answers, ...parsed.value };

  if (step === STEP_INDUSTRY && parsed.value) {
    // When selecting featured industries without "Other", clear other state unless extras present.
    if (!parsed.value.otherSelected) {
      next.otherIndustries = [];
      next.otherSelected = false;
    }
  }

  return next;
};
